import React, { useMemo } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { getFocusedRouteNameFromRoute, RouteProp } from '@react-navigation/native';
import { StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BottomTabParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import HomeIcon from '../icons/HomeIcon';
import ClubIcon from '../icons/ClubIcon';
import LedgerIcon from '../icons/LedgerIcon';
import WorkIcon from '../icons/WorkIcon';
import NetworkIcon from '../icons/NetworkIcon';

import HomeNavigator from '../../navigation/HomeNavigator';
import ClubsScreen from '../../screens/ClubsScreen';
import WorkNavigator from '../../navigation/WorkNavigator';
import LedgerNavigator from '../../navigation/LedgerNavigator';
import NetworkScreen from '../../screens/NetworkScreen';

const Tab = createBottomTabNavigator<BottomTabParamList>();

const BottomTabNavigator: React.FC = () => {
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();

  const activeLabelColor = '#A67D2E';

  const baseTabBarStyle = useMemo(() => ([
    styles.tabBar,
    {
      height: 70 + insets.bottom,
      paddingBottom: insets.bottom,
      backgroundColor: theme.background,
      borderTopColor: theme.border,
      shadowOpacity: 0,
      shadowOffset: { width: 0, height: 0 },
      shadowRadius: 0,
      elevation: 0,
    },
  ]), [insets.bottom, theme.background, theme.border]);

  const getClubsTabBarStyle = (route: RouteProp<BottomTabParamList, 'Clubs'>) => {
    const nestedRouteName = getFocusedRouteNameFromRoute(route) ?? 'ClubsHome';
    if (nestedRouteName === 'ClubChat') {
      return { display: 'none' as const };
    }
    return baseTabBarStyle;
  };

  return (
    <Tab.Navigator
      initialRouteName="Home"
      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarIcon: ({ focused, color, size }) => {
          if (route.name === 'Home') {
            return <HomeIcon focused={focused} color={color} size={size} />;
          }
          
          if (route.name === 'Clubs') {
            return <ClubIcon focused={focused} color={color} size={size} />;
          }
          
          if (route.name === 'Ledger') {
            return <LedgerIcon focused={focused} color={color} size={size} />;
          }
          
          if (route.name === 'Work') {
            return <WorkIcon focused={focused} color={color} size={size} />;
          }
          
          if (route.name === 'Network') {
            return <NetworkIcon focused={focused} color={color} size={size} />;
          }

          return null;
        },
        tabBarActiveTintColor: activeLabelColor,
        tabBarInactiveTintColor: theme.textSecondary,
        tabBarLabelStyle: styles.tabLabel,
        tabBarStyle: baseTabBarStyle,
        tabBarItemStyle: styles.tabItem,
      })}
    >
      <Tab.Screen 
        name="Clubs" 
        component={ClubsScreen}
        options={({ route }) => ({
          tabBarLabel: 'Circles',
          tabBarStyle: getClubsTabBarStyle(route),
        })}
      />
      <Tab.Screen 
        name="Work" 
        component={WorkNavigator}
      />
      <Tab.Screen 
        name="Home" 
        component={HomeNavigator}
      />
      <Tab.Screen 
        name="Ledger" 
        component={LedgerNavigator}
      />
      <Tab.Screen 
        name="Network" 
        component={NetworkScreen}
      />
    </Tab.Navigator>
  );
};

const styles = StyleSheet.create({
  tabBar: {
    borderTopWidth: 1,
    paddingTop: 8,
    shadowOpacity: 0,
    elevation: 0,
  },
  tabLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  tabItem: {
    paddingVertical: 4,
  },
});

export default BottomTabNavigator;
